import { getOwnerToken, OWNER_TOKEN_HEADER } from './ownerToken'
// Kind definitions and heading logic live with the marker artwork so the live
// map and server-rendered images can't drift apart.
import { isDirectional, rotatesBody, type PinKind } from './pinMarker'

interface MultilingualText {
  en: string
  fr: string
  ar: string
}

interface PinData {
  id: string
  lng: number
  lat: number
  description: string // Deprecated: for backward compatibility
  descriptions?: MultilingualText // New multilingual descriptions
  createdAt?: string // Creation timestamp
  kind?: PinKind
  /**
   * Direction of travel for car/velo pins, in degrees clockwise from north.
   * `null` (or absent) means the vehicle is parked / not moving.
   */
  heading?: number | null
  /** Set by the server per request: was this pin created by this browser? */
  owned?: boolean
}

interface ApiResponse<T = any> {
  success: boolean
  data?: T
  pins?: T[]
  error?: string
  message?: string
}

class PinService {
  private baseUrl = window.location.origin

  private async fetchApi<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          // Identifies which pins belong to this browser
          [OWNER_TOKEN_HEADER]: getOwnerToken(),
          ...options.headers,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error(`API call failed for ${endpoint}:`, error)
      throw error
    }
  }

  async loadPins(): Promise<PinData[]> {
    try {
      const response = await this.fetchApi<PinData[]>('/api/pins')
      return response.pins || []
    } catch (error) {
      console.error('Failed to load pins:', error)
      // Return empty array if backend is not available
      return []
    }
  }

  async addPin(pin: PinData, sourceLanguage?: string): Promise<void> {
    await this.fetchApi('/api/pins', {
      method: 'POST',
      body: JSON.stringify({
        pin,
        sourceLanguage: sourceLanguage?.slice(0, 2),
      }),
    })
  }

  async updatePin(
    pinId: string,
    updates: Partial<
      Pick<PinData, 'lng' | 'lat' | 'description' | 'descriptions' | 'heading'>
    >,
    // heading may be explicitly null to mark a vehicle as parked
    sourceLanguage?: string
  ): Promise<void> {
    await this.fetchApi('/api/pins', {
      method: 'PUT',
      body: JSON.stringify({
        pinId,
        updates,
        sourceLanguage: sourceLanguage?.slice(0, 2),
      }),
    })
  }

  async deletePin(pinId: string): Promise<void> {
    await this.fetchApi('/api/pins', {
      method: 'DELETE',
      body: JSON.stringify({
        pinId,
      }),
    })
  }

  async checkHealth(): Promise<boolean> {
    try {
      const response = await this.fetchApi('/api/health')
      return response.success !== false
    } catch (error) {
      return false
    }
  }
}

export const pinService = new PinService()
export { isDirectional, rotatesBody }
export type { PinData, MultilingualText, PinKind }
